/* <vintage-carousel>: crossfade-first image carousel.
   - Slides are stacked absolutely; transitions animate opacity
     (crossfade / fade-blank / kenburns) or translateX+opacity (slide).
   - Auto-advances on data-interval (0 = off). A+-style clickable dots; no arrows.
   - Pauses on hover/focus and while off-screen. Honours prefers-reduced-motion.
   - 1 slide => static (no dots / no autoplay). */
(function () {
  if (window.customElements.get('vintage-carousel')) return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class VintageCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-vcar-viewport]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.vcar__slide'));
      this.dotsEl = this.querySelector('[data-vcar-dots]');

      this.interval = parseInt(this.getAttribute('data-interval'), 10) || 0;
      this.duration = parseInt(this.getAttribute('data-duration'), 10) || 900;
      this.easing = this.getAttribute('data-easing') || 'cubic-bezier(0.4, 0, 0.2, 1)';
      this.style.setProperty('--vcar-dur', this.duration + 'ms');
      this.style.setProperty('--vcar-ease', this.easing);

      this.index = 0;
      if (this.slides.length) this.slides[0].classList.add('is-active');
      if (this.slides.length < 2) { if (this.dotsEl) this.dotsEl.hidden = true; return; }

      this._hovered = false;
      this._buildDots();
      this._bindSwipe();

      // When data-no-hover-pause is set, the carousel keeps auto-rolling on
      // hover/focus (used by the vintage page's relocated bottom carousel).
      if (!this.hasAttribute('data-no-hover-pause')) {
        this.addEventListener('mouseenter', () => { this._hovered = true; this._stop(); });
        this.addEventListener('mouseleave', () => { this._hovered = false; this._maybeStart(); });
        this.addEventListener('focusin', () => { this._hovered = true; this._stop(); });
        this.addEventListener('focusout', () => { this._hovered = false; this._maybeStart(); });
      }

      if ('IntersectionObserver' in window) {
        // Start paused; the observer fires for in-view elements too and flips this on,
        // so off-screen carousels never run a timer before the first callback.
        this._visible = false;
        this._io = new IntersectionObserver((entries) => {
          this._visible = entries[0].isIntersecting;
          if (this._visible) this._maybeStart(); else this._stop();
        }, { threshold: 0.25 });
        this._io.observe(this);
      } else {
        this._visible = true;
        this._maybeStart();
      }

      this._update();
    }

    disconnectedCallback() { this._stop(); if (this._io) this._io.disconnect(); }

    go(i) {
      var n = this.slides.length;
      if (!n) return;
      i = (i % n + n) % n;
      if (i === this.index) return;
      this.slides[this.index].classList.remove('is-active');
      this.index = i;
      this.slides[this.index].classList.add('is-active');
      this._update();
    }

    next() { this.go(this.index + 1); }

    _maybeStart() {
      if (REDUCE || !this.interval || this._hovered || this._editorHold || this._visible === false) return;
      this._stop();
      this._timer = setInterval(() => this.next(), this.interval);
    }
    _stop() { if (this._timer) { clearInterval(this._timer); this._timer = null; } }
    _restart() { this._stop(); this._maybeStart(); }

    _update() {
      if (!this.dotsEl) return;
      var dots = this.dotsEl.children;
      for (var k = 0; k < dots.length; k++) {
        var active = k === this.index;
        dots[k].classList.toggle('is-active', active);
        dots[k].setAttribute('aria-current', active ? 'true' : 'false');
      }
    }

    _buildDots() {
      if (!this.dotsEl) return;
      var self = this, frag = document.createDocumentFragment();
      this.slides.forEach(function (s, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'vcar__dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', 'Go to image ' + (i + 1));
        b.addEventListener('click', function () { self.go(i); self._restart(); });
        frag.appendChild(b);
      });
      this.dotsEl.appendChild(frag);
    }

    _bindSwipe() {
      var self = this, startX = null;
      this.viewport.addEventListener('pointerdown', function (e) { startX = e.clientX; }, { passive: true });
      this.viewport.addEventListener('pointerup', function (e) {
        if (startX === null) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 40) { if (dx < 0) self.next(); else self.go(self.index - 1); self._restart(); }
        startX = null;
      });
    }
  }

  window.customElements.define('vintage-carousel', VintageCarousel);

  // Theme editor: selecting a Slide block shows that slide and holds autoplay until the block is
  // deselected (these events only fire inside the Shopify theme editor). Guarded by the
  // customElements check above, so the listeners are bound once per page.
  function slideTarget(e) {
    var slide = e.target && e.target.closest && e.target.closest('.vcar__slide');
    var car = slide && slide.closest('vintage-carousel');
    return car && car.slides ? { car: car, i: car.slides.indexOf(slide) } : null;
  }
  document.addEventListener('shopify:block:select', function (e) {
    var t = slideTarget(e); if (!t || t.i < 0) return;
    t.car._editorHold = true; t.car._stop(); t.car.go(t.i);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var t = slideTarget(e); if (!t) return;
    t.car._editorHold = false; t.car._maybeStart();
  });
})();
